import { liveSubtypes } from '@accedo/one-plugin-sdk';
import { cloneDeep } from '@dynamic/ui';
import { proxy, useSnapshot } from 'valtio';

import { historyService } from '#/client/api';
import {
  FeedResponse,
  HistoryAllItem,
  Item,
  MediaType,
  ResourceType,
  WatchHistoryItemResponse,
} from '#/client/models';
import { devtoolsApi } from '#/dev/devtools';
import { isAudio } from '#/hooks/playback/utils';

interface HistoryStore {
  history: { [id: string]: HistoryAllItem };
  isHistoryFetched: boolean;
}

export const RECORDING_THRESHOLD_S = 20;

/** Store for watch history */
export const historyStore = proxy<HistoryStore>({
  history: {},
  isHistoryFetched: false,
});

/** Store actions for watch history */
export const historyActions = {
  // load the full history and cache the results. used during app startup.
  async loadAll() {
    try {
      // fetch all history
      const { data } = await historyService.getAll();
      // update cache
      (data.data ?? []).forEach((historyItem) => {
        // clone the history item to prevent later updates mutate the original
        historyStore.history[historyItem.objectId] = cloneDeep(historyItem);
      });
      historyStore.isHistoryFetched = true;
    } catch (err) {
      console.warn('Watch history: loadAll failed', err);
    }
  },

  // get a page of the history feed, and update the cache too. used for watch history page.
  async getPage(page: number): Promise<FeedResponse> {
    return historyService.getPage(page).then(({ data: res }) => {
      // update cache
      (res.data ?? []).forEach((item) => {
        historyStore.history[item.id] = {
          objectId: item.id,
          objectType: item.type,
          watched: isAudio(item) ? item.audio?.watched : item.video?.watched,
        };
      });

      return res;
    });
  },

  // get history (from one core backend) for a single item, and update the cache too. used for details page.
  async getOne(
    resourceType?: ResourceType,
    resourceId?: string
  ): Promise<WatchHistoryItemResponse> {
    return historyService
      .getOne(resourceType, resourceId)
      .then(({ data: res }) => {
        const { data: item } = res;
        // update cache
        historyStore.history[item.id] = {
          objectId: item.id,
          objectType: item.type,
          watched: isAudio(item) ? item.audio?.watched : item.video?.watched,
        };

        return res;
      });
  },

  async removeFromHistory(itemId: string, mediaType: MediaType) {
    await historyService.remove(itemId, mediaType);
    console.debug('Watch history: removed from watch history', itemId);

    // update cache
    delete historyStore.history[itemId];
  },

  // updates the cached history entry for a single item, as we are playing it.
  update(item: Item, currentTime: number, duration: number) {
    if (currentTime < RECORDING_THRESHOLD_S) {
      return;
    }

    const cachedWatched = historyStore.history[item.id]?.watched;
    const audioOrVideo = isAudio(item) ? item.audio : item.video;

    if (!cachedWatched) {
      historyStore.history[item.id] = {
        objectId: item.id,
        objectType: item.type,
        watched: {
          progress: {
            finished:
              currentTime >= (audioOrVideo?.markers?.fullyWatched ?? duration),
            percentage: Math.floor((currentTime / duration) * 100),
            seconds: currentTime,
          },
        },
      };

      return;
    }

    cachedWatched.progress = {
      finished: currentTime >= (audioOrVideo?.markers?.fullyWatched ?? duration),
      percentage: Math.floor((currentTime / duration) * 100),
      seconds: currentTime,
    };
  },

  // Get a single history item from the history store cache.
  getOneFromHistory(id: string) {
    return historyStore.history[id];
  },

  // empties the cache. used for testing.
  reset() {
    historyStore.history = {};
    historyStore.isHistoryFetched = false;
  },

  // clone the cached `watched` object into all items. this is needed for history related features on browse pages.
  cloneWithWatchedObjects(items: Item[] | undefined) {
    if (!items) {
      return [];
    }

    return (
      items.map((item) => {
        const isAudioItem = isAudio(item);
        const audioOrVideoKey = isAudioItem ? 'audio' : 'video';

        // Live channels and programs have no resumable watch position, so skip injecting the watched object.
        if (
          (isAudioItem && !item.audio) ||
          (!isAudioItem && !item.video) ||
          liveSubtypes.has(item.subtype)
        ) {
          return item;
        }

        const cachedWatched = historyStore.history[item.id]?.watched;

        return {
          ...item,
          [audioOrVideoKey]: {
            ...item[audioOrVideoKey],
            watched: cachedWatched ?? item[audioOrVideoKey]?.watched,
          },
        };
      }) ?? []
    );
  },
};

export const useHistoryStore = () => useSnapshot(historyStore) as HistoryStore;

devtoolsApi.setStore('history', historyStore, historyActions);
